import { useTheme } from "@/theme/ThemeProvider";
import { Pressable, Text, View, type ViewStyle } from "react-native";

/**
 * Dichte Arbeitszeile – klare Titel-/Meta-Hierarchie, wenig vertikale Höhe.
 */
export function DenseRow({
  leading,
  title,
  meta,
  trailing,
  onPress,
  emphasized = false,
  style,
  children,
}: {
  leading?: React.ReactNode;
  title: string;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
  onPress?: () => void;
  emphasized?: boolean;
  style?: ViewStyle;
  children?: React.ReactNode;
}) {
  const { theme } = useTheme();

  const content = (
    <View
      style={[
        {
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          maxWidth: "100%",
          gap: theme.space.sm,
          paddingVertical: 7,
          paddingHorizontal: theme.space.md,
          borderBottomWidth: 1,
          borderBottomColor: theme.color.border,
          backgroundColor: emphasized
            ? theme.color.accentBg
            : theme.color.surface,
        },
        style,
      ]}
    >
      {leading ? <View style={{ flexShrink: 0 }}>{leading}</View> : null}
      <View style={{ flex: 1, minWidth: 0, gap: 1 }}>
        <Text
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.small,
            fontWeight: emphasized ? "700" : "600",
            letterSpacing: -0.1,
          }}
          numberOfLines={1}
        >
          {title}
        </Text>
        {meta ? (
          typeof meta === "string" ? (
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "500",
              }}
              numberOfLines={1}
            >
              {meta}
            </Text>
          ) : (
            meta
          )
        ) : null}
        {children}
      </View>
      {trailing ? (
        <View style={{ flexShrink: 1, maxWidth: "100%", minWidth: 0 }}>{trailing}</View>
      ) : null}
    </View>
  );

  if (!onPress) return content;
  return <Pressable onPress={onPress}>{content}</Pressable>;
}

/** Label + Wert für Info-Karten – Label als Meta, Wert als Primärtext. */
export function InfoField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const { theme } = useTheme();
  return (
    <View style={{ gap: 2, minWidth: 0, maxWidth: "100%", width: "100%" }}>
      <Text
        style={{
          color: theme.color.muted,
          fontSize: theme.fontSize.tiny,
          fontWeight: "700",
          letterSpacing: 0.35,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          color: theme.color.text,
          fontSize: theme.fontSize.body,
          fontWeight: "600",
        }}
      >
        {value || "–"}
      </Text>
    </View>
  );
}
